import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";

interface ActivityItem {
  id: string;
  customerName: string;
  staffName: string;
  source: "email" | "qr";
  rating?: number;
  status: "sent" | "opened" | "clicked" | "reviewed" | "feedback";
  date: string;
}

interface RecentActivityTableProps {
  activities: ActivityItem[];
}

export function RecentActivityTable({ activities }: RecentActivityTableProps) {
  const getStatusBadge = (status: ActivityItem["status"]) => {
    const variants: Record<ActivityItem["status"], { label: string; variant: "default" | "secondary" | "outline" }> = {
      sent: { label: "Sent", variant: "outline" },
      opened: { label: "Opened", variant: "secondary" },
      clicked: { label: "Clicked", variant: "secondary" },
      reviewed: { label: "Reviewed", variant: "default" },
      feedback: { label: "Feedback", variant: "outline" },
    };
    const { label, variant } = variants[status];
    return <Badge variant={variant}>{label}</Badge>;
  };

  return (
    <div className="rounded-md border" data-testid="recent-activity-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Staff</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Rating</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {activities.map((activity) => (
            <TableRow key={activity.id} data-testid={`activity-row-${activity.id}`}>
              <TableCell className="font-medium">{activity.customerName}</TableCell>
              <TableCell className="text-muted-foreground">{activity.staffName}</TableCell>
              <TableCell>
                <Badge variant="outline" className="capitalize">
                  {activity.source}
                </Badge>
              </TableCell>
              <TableCell>
                {activity.rating ? (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span>{activity.rating}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>{getStatusBadge(activity.status)}</TableCell>
              <TableCell className="text-right text-muted-foreground">
                {activity.date}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
